// START IMPORTS
import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack, useTheme } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  DateUtil,
  usePhoneFormatter,
} from '@ltvco/refresh-lib/utils';
import {
  DebugMenu,
  PhoneFraudReport,
  ReportLoading,
} from '@ltvco/refresh-lib/v1';
import {
  FraudPhoneOverview,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  ReportActionsWithDateUpdated,
  ReportSpamCallout,
  ReportRating,
  PotentialOwnerCta as PotentialOwnerCtaV2,
  PhoneFraudCommentsSection,
  ReportNullState,
} from '@ltvco/refresh-lib/v2';
import { useState } from 'react';

import { getFraudPhoneNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';

import type { ReportRouteProps } from '../types';
// END IMPORTS

export function PhoneFraudReportPage({ permalink }: ReportRouteProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const theme = useTheme();
  const { isHidden } = useSubscriptionAccessConfigContext();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [spam, setSpam] = useState<'unsure' | 'yes' | 'no'>('unsure');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  const rawPhone = queryResult?.data?.meta?.search_data?.phone_number ?? '';
  const formattedPhoneNumber = usePhoneFormatter(rawPhone, true);
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const report = new PhoneFraudReport(queryResult?.data);

  if (!report) return <ReportLoading menuItems={2} />;
  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }

  const reportType = 'phone-fraud';
  const {
    fraudScore,
    isActive,
    isDoNotCall,
    isPrepaid,
    isRecentAbuse,
    isRisky,
    isSpammer,
    isVOIP,
    isValid,
    lineType,
    location,
    userActivity,
    comments,
  } = report.data;

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';
  document.title = `${formattedPhoneNumber} - NumberGuru`;
  const fraudPhoneNavLinkDataV2 = getFraudPhoneNavLinkDataV2({
    commentsLength: report.data.comments?.length ?? 0,
  });
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Fraud Scan"
            headerTitle={formattedPhoneNumber}
            menuItems={
              fraudPhoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0}>
          <Box
            sx={{
              height: 30,
              backgroundColor: theme.palette.background.default,
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Phone Fraud"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <FraudPhoneOverview
            isRisky={isRisky}
            isDoNotCall={isDoNotCall}
            isSpammer={isSpammer}
            isVOIP={isVOIP}
            isPrepaid={isPrepaid}
            isValid={isValid}
            activeStatus={isActive}
            isRecentAbuse={isRecentAbuse}
            userActivity={userActivity}
            number={formattedPhoneNumber || 'Unknown'}
            location={location?.address?.full || 'Unknown'}
            fraudScore={fraudScore || 0}
            lineType={lineType}
          />

          <Stack spacing={2}>
            <ReportSpamCallout
              permalink={permalink}
              spam={spam}
              setSpam={setSpam}
            />
            {!isHidden('phone') && (
              <PotentialOwnerCtaV2
                number={formattedPhoneNumber}
                reportType="phone"
              />
            )}
            <PhoneFraudCommentsSection
              comments={comments}
              formatted_number={formattedPhoneNumber}
              permalink={permalink}
              spam={spam}
              setSpam={setSpam}
            />
            <ReportRating
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
          </Stack>
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Fraud Scan"
        headerTitle={formattedPhoneNumber}
        menuItems={
          fraudPhoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
      />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
  // END RENDER
}
